import React, { useEffect, useMemo, useState } from 'react';
import { normalizeTrack } from '@/state/trackModel';
import { applyFilterPreset, FILTER_PRESETS, lowPassToPosition, positionToLowPass, formatCutoff } from '@/state/filterPresets';
import { MixRange } from './MixControls';

export const EFFECT_LABELS = { eq: 'EQ', reverb: 'Reverb', echo: 'Echo', punch: 'Punch' };

export function toneToEq(tone) {
  const value = Math.max(-12, Math.min(12, Number(tone) || 0));
  return { low: -value, mid: 0, high: value };
}

export function eqToTone(eq = {}) {
  return Math.max(-12, Math.min(12, (Number(eq.high || 0) - Number(eq.low || 0)) / 2));
}

const Knob = ({ testId, label, value, min, max, step, onChange, onCommit, format }) => (
  <label className="ba-fx-knob">
    <span>{label}</span>
    <MixRange testId={testId} label={label} min={min} max={max} step={step} value={value} onDraft={onChange} onCommit={onCommit} />
    <output>{format(value)}</output>
  </label>
);

export function EffectsPopover({ track, initialEffect = 'eq', onDraft, onCommit, onClose }) {
  const cleanEffects = useMemo(() => normalizeTrack(track).effects, [track]);
  const [effects, setEffects] = useState(cleanEffects);
  const [selected, setSelected] = useState(initialEffect);
  useEffect(() => setEffects(cleanEffects), [cleanEffects]);

  const update = (next) => {
    effectsRef.current = next;
    setEffects(next);
    onDraft?.(track.id, { effects: next });
  };
  const effectsRef = React.useRef(effects);
  effectsRef.current = effects;
  const commit = () => onCommit?.(track.id, { effects: effectsRef.current });
  const amountKnob = selected !== 'eq' && <Knob testId={`fx-${selected}-${track.id}`} label={EFFECT_LABELS[selected]} value={effects[selected]} min={0} max={1} step={0.01}
    onChange={(value) => update({ ...effects, [selected]: value })} onCommit={commit} format={(value) => `${Math.round(value * 100)}%`} />;

  return (
    <div data-testid={`effects-popover-${track.id}`} className="ba-effects-popover" role="dialog" aria-label={`${track.name} effects`}>
      <div className="ba-effects-title"><strong>{track.name}</strong><span>Effects</span>{onClose && <button aria-label="Close effects" onClick={onClose}>×</button>}</div>
      <div className="ba-effects-tabs">
        {Object.entries(EFFECT_LABELS).map(([id, label]) => <button key={id} data-testid={`fx-tab-${id}-${track.id}`} className={selected === id ? 'active' : ''} onClick={() => setSelected(id)}>{label}</button>)}
      </div>
      <div className="ba-effects-controls">
        {selected === 'eq' && <>
          <div className="ba-filter-presets" aria-label="EQ presets">
            {Object.entries(FILTER_PRESETS).map(([id, preset]) => <button key={id} onClick={() => { update(applyFilterPreset(effects, id)); commit(); }}>{preset.label}</button>)}
          </div>
          {['low', 'mid', 'high'].map(band => <Knob key={band} testId={`fx-eq-${band}-${track.id}`} label={band[0].toUpperCase() + band.slice(1)} value={effects.eq[band]} min={-12} max={12} step={0.5}
            onChange={value => update({ ...effects, eq: { ...effects.eq, [band]: value } })} onCommit={commit} format={value => `${value > 0 ? '+' : ''}${value} dB`} />)}
          <Knob testId={`fx-highpass-${track.id}`} label="High-pass" value={effects.highPassHz} min={0} max={1000} step={10}
            onChange={value => update({ ...effects, highPassHz: value })} onCommit={commit} format={value => value > 0 ? `${value} Hz` : 'Off'} />
          <Knob testId={`fx-lowpass-${track.id}`} label="Low-pass · cuts highs" value={lowPassToPosition(effects.lowPassHz)} min={0} max={100} step={0.1}
            onChange={value => update({ ...effects, lowPassHz: positionToLowPass(value) })} onCommit={commit} format={() => formatCutoff(effects.lowPassHz)} />
          <p className="ba-eq-help">High-pass cuts lows. Low-pass cuts highs. Move low-pass left for a darker sound; fully right turns it off.</p>
          <details className="ba-filter-more"><summary>More filter controls</summary>
            <Knob testId={`fx-resonance-${track.id}`} label="Resonance" value={effects.lowPassResonance} min={0} max={1} step={0.01}
              onChange={value => update({ ...effects, lowPassResonance: value })} onCommit={commit} format={value => `${Math.round(value * 100)}%`} />
            <p className="ba-eq-help">Adds emphasis near the low-pass cutoff. Start gently; low-pass must be on.</p>
          </details>
        </>}
        {amountKnob}
      </div>
    </div>
  );
}

export default EffectsPopover;
